export const CATALOG_COLUMN_COUNT = 3;

/**
 * API group indexes are strings ("11", "62", "72"), never treat as bare numbers.
 * Coerce number|string → trimmed digit string (e.g. 62 → "62").
 */
export function normalizeGridIndexString(index) {
  if (index == null) return "";
  const raw = String(index).trim();
  if (!raw) return "";
  // Reject non-digit tokens (keep leading zeros so "062" can still parse)
  if (!/^\d+$/.test(raw)) return "";
  return raw;
}

/**
 * Parse API grid index string: "11" → row 0 col 0, "12" → row 0 col 1, "62" → row 5 col 1.
 * Last digit = column (1–3); remaining digits = 1-based row.
 */
export function parseGridIndex(index, columnCount = CATALOG_COLUMN_COUNT) {
  const raw = normalizeGridIndexString(index);
  // Need at least row digit + column digit ("11", "62", "102", …)
  if (raw.length < 2) return null;

  const colDigit = Number(raw.slice(-1));
  const rowNumber = Number(raw.slice(0, -1));

  if (
    Number.isFinite(colDigit) &&
    colDigit >= 1 &&
    colDigit <= columnCount &&
    Number.isFinite(rowNumber) &&
    rowNumber >= 1
  ) {
    return { row: rowNumber - 1, col: colDigit - 1 };
  }

  return null;
}

/** Build grid position index string from 0-based row/col (always a string). */
export function toGridIndex(rowIndex, columnIndex) {
  return String(`${Number(rowIndex) + 1}${Number(columnIndex) + 1}`);
}

/** Compare two grid index strings by row, then column (not numeric Number()). */
export function compareGridIndexes(a, b, columnCount = CATALOG_COLUMN_COUNT) {
  const pa = parseGridIndex(a, columnCount);
  const pb = parseGridIndex(b, columnCount);
  if (pa && pb) {
    if (pa.row !== pb.row) return pa.row - pb.row;
    if (pa.col !== pb.col) return pa.col - pb.col;
    return 0;
  }
  if (pa && !pb) return -1;
  if (!pa && pb) return 1;
  return normalizeGridIndexString(a).localeCompare(
    normalizeGridIndexString(b),
    undefined,
    { numeric: true }
  );
}

/** Strict string equality after normalize ( "62" === 62 === "062" via parse→toGrid ). */
export function sameGridIndex(a, b, columnCount = CATALOG_COLUMN_COUNT) {
  const pa = parseGridIndex(a, columnCount);
  const pb = parseGridIndex(b, columnCount);
  if (pa && pb) {
    return pa.row === pb.row && pa.col === pb.col;
  }
  return normalizeGridIndexString(a) === normalizeGridIndexString(b);
}

/**
 * Place groups into columns from API string index (11 = r1c1, 12 = r1c2, 62 = r6c2, …).
 * Column = last digit; vertical order = row ascending (packed — no empty gap slots).
 * Duplicate indexes stack in the same column (all groups kept).
 */
export function groupsToColumnsByGridIndex(groups = [], columnCount = CATALOG_COLUMN_COUNT) {
  const columnEntries = Array.from({ length: columnCount }, () => []);
  const unplaced = [];

  groups.forEach((group) => {
    const pos = parseGridIndex(group?.index, columnCount);
    if (!pos) {
      unplaced.push(group);
      return;
    }
    // Keep index as canonical string ("62"), even if API sent number 62
    const canonical = {
      ...group,
      index: toGridIndex(pos.row, pos.col),
    };
    columnEntries[pos.col].push({ group: canonical, row: pos.row });
  });

  const columns = columnEntries.map((entries) => {
    entries.sort(
      (a, b) =>
        a.row - b.row ||
        Number(a.group.id) - Number(b.group.id) ||
        String(a.group.name || "").localeCompare(String(b.group.name || ""))
    );
    return entries.map((entry) => entry.group);
  });

  unplaced
    .slice()
    .sort(
      (a, b) =>
        compareGridIndexes(a.index, b.index, columnCount) ||
        Number(a.id) - Number(b.id)
    )
    .forEach((group) => {
      const shortest = columns.reduce(
        (minIdx, col, i, arr) => (col.length < arr[minIdx].length ? i : minIdx),
        0
      );
      columns[shortest].push(group);
    });

  return columns;
}

/** Initial equal grid from flat row-major API list (index 1,2,3…). */
export function splitIntoColumns(items, columnCount = CATALOG_COLUMN_COUNT) {
  const columns = Array.from({ length: columnCount }, () => []);
  items.forEach((item, index) => {
    columns[index % columnCount].push({ item, index });
  });
  return columns;
}

/** Flatten columns row-by-row (only filled cells) for API index order. */
export function columnsToFlat(columns) {
  const columnCount = columns.length;
  const maxLen = Math.max(0, ...columns.map((col) => col.length));
  const result = [];
  for (let row = 0; row < maxLen; row += 1) {
    for (let col = 0; col < columnCount; col += 1) {
      if (columns[col][row]) result.push(columns[col][row]);
    }
  }
  return result;
}

const COLUMN_LAYOUT_STORAGE_KEY = "mr-phone-pricing-catalog-column-layout";

export const saveColumnLayout = (columns) => {
  try {
    const themes = {};
    columns.forEach((col) => {
      col.forEach((group) => {
        if (group?.id != null && group.themeKey) {
          themes[String(group.id)] = group.themeKey;
        }
      });
    });
    localStorage.setItem(
      COLUMN_LAYOUT_STORAGE_KEY,
      JSON.stringify({
        columns: columns.map((col) =>
          col.filter(Boolean).map((g) => g.id)
        ),
        themes,
      })
    );
  } catch {
    // ignore quota / private mode
  }
};

export const readColumnLayout = () => {
  try {
    const raw = localStorage.getItem(COLUMN_LAYOUT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return { columns: parsed, themes: null };
    }
    if (parsed && Array.isArray(parsed.columns)) {
      return {
        columns: parsed.columns,
        themes: parsed.themes && typeof parsed.themes === "object" ? parsed.themes : null,
      };
    }
    return null;
  } catch {
    return null;
  }
};

/** Build column stacks from stored ids; append new groups to the shortest column. */
export function applyStoredColumnLayout(
  groups,
  storedColumnIds,
  themeById = {},
  columnCount = CATALOG_COLUMN_COUNT
) {
  const byId = new Map(groups.map((group) => [String(group.id), group]));
  const used = new Set();
  const columns = Array.from({ length: columnCount }, () => []);

  const withTheme = (group) => {
    const themeKey = themeById[group.id] ?? themeById[String(group.id)] ?? group.themeKey;
    return themeKey ? { ...group, themeKey } : group;
  };

  (storedColumnIds ?? []).forEach((colIds, col) => {
    if (col >= columnCount) return;
    colIds.forEach((id) => {
      const group = byId.get(String(id));
      if (!group) return;
      used.add(String(id));
      columns[col].push(withTheme(group));
    });
  });

  groups.forEach((group) => {
    if (used.has(String(group.id))) return;
    const shortest = columns.reduce(
      (minIdx, col, i, arr) => (col.length < arr[minIdx].length ? i : minIdx),
      0
    );
    columns[shortest].push(withTheme(group));
  });

  return columns;
}

/** First load fallback: round-robin (prefer groupsToColumnsByGridIndex when indexes exist). */
export function groupsToColumns(groups, columnCount = CATALOG_COLUMN_COUNT) {
  return groupsToColumnsByGridIndex(groups, columnCount);
}

const cloneColumns = (columns) => columns.map((col) => [...col]);

const isSameId = (a, b) => String(a) === String(b);

const findGroupColumnIndex = (columns, groupId) => {
  for (let col = 0; col < columns.length; col += 1) {
    const index = columns[col].findIndex(
      (g) => g != null && isSameId(g.id, groupId)
    );
    if (index >= 0) return { col, index };
  }
  return null;
};

export const columnsOrderKey = (columns) =>
  columns
    .map((col) => col.map((g) => (g?.id != null ? String(g.id) : "_")).join("-"))
    .join("|");

/**
 * Move a group before/after a target group. Works within one column (up/down)
 * and across columns (left/right).
 */
export function moveGroupInColumns(columns, srcId, tgtId, after = false) {
  const cols = cloneColumns(columns);
  const src = findGroupColumnIndex(cols, srcId);
  const tgt = findGroupColumnIndex(cols, tgtId);
  if (!src || !tgt || isSameId(srcId, tgtId)) return columns;

  const [moved] = cols[src.col].splice(src.index, 1);
  let insertAt = tgt.index + (after ? 1 : 0);
  if (src.col === tgt.col && src.index < insertAt) {
    insertAt -= 1;
  }

  cols[tgt.col].splice(Math.min(insertAt, cols[tgt.col].length), 0, moved);
  return cols;
}

/** Move a group into a column at a specific index (append when insertAt < 0). */
export function moveGroupToColumnAt(columns, srcId, targetCol, insertAt = -1) {
  const cols = cloneColumns(columns);
  const src = findGroupColumnIndex(cols, srcId);
  if (!src || targetCol < 0 || targetCol >= cols.length) return columns;

  const [moved] = cols[src.col].splice(src.index, 1);
  let at = insertAt < 0 ? cols[targetCol].length : insertAt;
  if (src.col === targetCol && src.index < at) {
    at -= 1;
  }
  cols[targetCol].splice(Math.min(Math.max(at, 0), cols[targetCol].length), 0, moved);
  return cols;
}

/** Move a group into another column (append). */
export function moveGroupToColumn(columns, srcId, targetCol) {
  return moveGroupToColumnAt(columns, srcId, targetCol, -1);
}

/** @deprecated Use moveGroupInColumns(columns, srcId, tgtId, false) */
export function insertGroupInColumns(columns, srcId, tgtId) {
  return moveGroupInColumns(columns, srcId, tgtId, false);
}
